
import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  Pagination, 
  PaginationContent, 
  PaginationItem, 
  PaginationLink, 
  PaginationNext, 
  PaginationPrevious 
} from "@/components/ui/pagination";
import { Badge } from "@/components/ui/badge";
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useForm } from "react-hook-form";
import { useToast } from "@/hooks/use-toast";
import { 
  Search, 
  PlusCircle, 
  FileEdit, 
  Trash, 
  Loader2,
  AlertTriangle,
} from "lucide-react";
import { questionTypesAPI } from "@/services/api";
import { format } from "date-fns";

export default function QuestionTypes() {
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentType, setCurrentType] = useState(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const form = useForm({
    defaultValues: {
      name: "",
      identifier: "",
      description: "",
      active: true,
    }
  });

  const editForm = useForm({
    defaultValues: {
      name: "",
      identifier: "",
      description: "",
      active: true,
    }
  });

  const { data, isLoading, isError } = useQuery({
    queryKey: ['questionTypes', page, limit, searchTerm],
    queryFn: () => questionTypesAPI.getAll({ page, limit, search: searchTerm }),
    keepPreviousData: true,
  });

  const createMutation = useMutation({
    mutationFn: (newType) => questionTypesAPI.create(newType),
    onSuccess: () => {
      queryClient.invalidateQueries('questionTypes');
      setIsAddModalOpen(false);
      toast({
        title: "Success",
        description: "Question type created successfully",
      });
      resetForm();
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to create question type",
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => questionTypesAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries('questionTypes');
      setIsEditModalOpen(false);
      toast({
        title: "Success",
        description: "Question type updated successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to update question type",
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => questionTypesAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries('questionTypes');
      toast({
        title: "Success",
        description: "Question type deleted successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to delete question type",
        variant: "destructive",
      });
    },
  });

  useEffect(() => {
    if (currentType) {
      editForm.reset({
        name: currentType.name,
        identifier: currentType.identifier,
        description: currentType.description || "",
        active: currentType.active,
      });
    }
  }, [currentType, editForm]);

  const resetForm = () => {
    form.reset({
      name: "",
      identifier: "",
      description: "",
      active: true,
    });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    queryClient.invalidateQueries('questionTypes');
  };

  const onSubmit = (formData) => {
    createMutation.mutate(formData);
  };

  const onEditSubmit = (formData) => {
    updateMutation.mutate({
      id: currentType._id,
      data: formData
    });
  };

  const handleEdit = (type) => {
    setCurrentType(type);
    setIsEditModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!confirm("Are you sure you want to delete this question type?")) return;
    deleteMutation.mutate(id);
  };

  // Generate an identifier from the name
  const generateIdentifier = (name) => {
    if (!name) return "";
    return name
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "_")
      .replace(/_+/g, "_")
      .replace(/^_|_$/g, "");
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Question Types</h1>
        <Button onClick={() => setIsAddModalOpen(true)}>
          <PlusCircle className="w-4 h-4 mr-2" />
          Add Question Type
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle>Manage Question Types</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSearch} className="mb-6 flex items-center space-x-2">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
              <Input
                type="search"
                placeholder="Search question types..."
                className="pl-8 w-full"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Button type="submit">Search</Button>
          </form>

          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <Loader2 className="h-12 w-12 animate-spin text-primary" />
            </div>
          ) : isError ? (
            <div className="text-center p-6 text-red-500">
              Failed to load question types. Please try again.
            </div>
          ) : (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Identifier</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created On</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data?.data?.questionTypes && data.data.questionTypes.length > 0 ? (
                      data.data.questionTypes.map((type) => (
                        <TableRow key={type._id}>
                          <TableCell className="font-medium">{type.name}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="bg-gray-100 font-mono">
                              {type.identifier}
                            </Badge>
                          </TableCell>
                          <TableCell className="max-w-[200px] truncate">{type.description || "N/A"}</TableCell>
                          <TableCell>
                            <Badge variant={type.active ? "success" : "secondary"}>
                              {type.active ? "Active" : "Inactive"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {type.createdDate ? format(new Date(type.createdDate), 'MMM dd, yyyy') : 'N/A'}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end space-x-2">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleEdit(type)}
                              >
                                <FileEdit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-red-500 hover:text-red-700"
                                onClick={() => handleDelete(type._id)}
                              >
                                <Trash className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center p-4 text-gray-500">
                          No question types found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>

              {data?.data?.totalPages > 1 && (
                <Pagination className="mt-4">
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        onClick={() => setPage((p) => Math.max(p - 1, 1))}
                        disabled={page === 1}
                        className={page === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                      />
                    </PaginationItem>

                    {[...Array(data.data.totalPages).keys()].map((i) => {
                      const pageNumber = i + 1;
                      return (
                        <PaginationItem key={pageNumber}>
                          <PaginationLink
                            isActive={page === pageNumber}
                            onClick={() => setPage(pageNumber)}
                            className="cursor-pointer"
                          >
                            {pageNumber}
                          </PaginationLink>
                        </PaginationItem>
                      );
                    })}

                    <PaginationItem>
                      <PaginationNext
                        onClick={() => setPage((p) => Math.min(p + 1, data.data.totalPages))}
                        disabled={page === data.data.totalPages}
                        className={page === data.data.totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Add Question Type Dialog */}
      <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Add New Question Type</DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                rules={{ 
                  required: "Question type name is required",
                  onChange: (e) => {
                    form.setValue("identifier", generateIdentifier(e.target.value));
                  }
                }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Type Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter question type name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="identifier"
                rules={{ 
                  required: "Identifier is required",
                  pattern: {
                    value: /^[A-Z0-9_]+$/,
                    message: "Identifier should only contain uppercase letters, numbers, and underscores"
                  }
                }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Identifier</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="QUESTION_TYPE_IDENTIFIER" 
                        className="font-mono"
                        {...field} 
                      />
                    </FormControl>
                    <div className="text-xs text-muted-foreground">
                      Used in code. Uppercase with underscores.
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Enter question type description" 
                        className="resize-none" 
                        {...field} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="active"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Status</FormLabel>
                      <div className="text-sm text-gray-500">
                        Set the question type as active or inactive
                      </div>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    setIsAddModalOpen(false);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={createMutation.isLoading}>
                  {createMutation.isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Add Question Type
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Edit Question Type Dialog */}
      <Dialog open={isEditModalOpen} onOpenChange={(open) => {
        setIsEditModalOpen(open);
        if (!open) setCurrentType(null);
      }}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Edit Question Type</DialogTitle>
          </DialogHeader>
          <Form {...editForm}>
            <form onSubmit={editForm.handleSubmit(onEditSubmit)} className="space-y-4">
              <FormField
                control={editForm.control}
                name="name"
                rules={{ 
                  required: "Question type name is required" 
                }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Type Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter question type name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={editForm.control}
                name="identifier"
                rules={{ 
                  required: "Identifier is required",
                  pattern: {
                    value: /^[A-Z0-9_]+$/,
                    message: "Identifier should only contain uppercase letters, numbers, and underscores"
                  }
                }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Identifier</FormLabel>
                    <div className="flex items-center gap-2">
                      <FormControl>
                        <Input 
                          placeholder="QUESTION_TYPE_IDENTIFIER" 
                          className="font-mono"
                          {...field} 
                        />
                      </FormControl>
                      {currentType && currentType.identifier !== field.value && (
                        <AlertTriangle className="h-5 w-5 text-amber-500" />
                      )}
                    </div>
                    {currentType && currentType.identifier !== field.value && (
                      <div className="text-xs text-amber-500">
                        Warning: Changing the identifier may affect existing questions.
                      </div>
                    )}
                    <div className="text-xs text-muted-foreground">
                      Used in code. Uppercase with underscores.
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={editForm.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Enter question type description" 
                        className="resize-none" 
                        {...field} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={editForm.control}
                name="active"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Status</FormLabel>
                      <div className="text-sm text-gray-500">
                        Set the question type as active or inactive
                      </div>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    setIsEditModalOpen(false);
                    setCurrentType(null);
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={updateMutation.isLoading}>
                  {updateMutation.isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Update Question Type
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
